import { styled } from '@linaria/react';
import type { ReactNode } from 'react';

type IconTileSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface IconTileProps {
  /** The glyph or logo shown in the tile. */
  children: ReactNode;
  /** Tile background, a CSS color (usually a `--*-lightest` token). */
  background: string;
  /** `xs` 22px, `sm` 30px, `md` 36px, `lg` 44px, `xl` 52px. Defaults to `md`. */
  size?: IconTileSize;
}

/**
 * Renders the rounded colour tile that fronts a capability, connection, or
 * setting row with its icon or brand logo.
 *
 * @param {IconTileProps} props - The tile content, background, and size
 * @returns {JSX.Element} The icon tile
 *
 * @example
 * <IconTile background="var(--blue-lightest)" size="lg">
 *   <Icon name="cloud" size={24} color="var(--blue-darkest)" />
 * </IconTile>
 */
export function IconTile({ children, background, size = 'md' }: IconTileProps) {
  return (
    <Tile data-size={size} $background={background} aria-hidden="true">
      {children}
    </Tile>
  );
}

const Tile = styled.span<{ $background: string }>`
  display: flex;
  background: ${({ $background }) => $background};
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;

  &[data-size='xs'] {
    width: 22px;
    height: 22px;
    border-radius: 6px;
  }

  &[data-size='sm'] {
    width: 30px;
    height: 30px;
    border-radius: 8px;
  }

  &[data-size='lg'] {
    width: 44px;
    height: 44px;
    border-radius: 11px;
  }

  &[data-size='xl'] {
    width: 52px;
    height: 52px;
    border-radius: 14px;
  }
`;

export type { IconTileProps, IconTileSize };
